import { confirm } from '@/components/ui/confirmation-dialog';
import { MenuItem } from '@/types/menu-item';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Pressable, Text, View } from 'react-native';

interface AdminPendingItemRowProps {
  item: MenuItem;
  onApprove: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function AdminPendingItemRow({
  item,
  onApprove,
  onEdit,
  onDelete,
}: AdminPendingItemRowProps) {
  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete menu item?',
      description: `Delete "${item.name}"? This cannot be undone.`,
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      onDelete(item.id);
    }
  };

  const hasCheckIns = !!item.checkInCount && item.checkInCount > 0;

  const formattedDate = new Date(item.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <View
      className="bg-surface"
      style={{ borderRadius: 12, padding: 14, marginBottom: 10 }}
    >
      <View style={{ marginBottom: 10 }}>
        <Text className="text-foreground" style={{ fontSize: 16, fontWeight: '600' }}>
          {item.name}
        </Text>
        {item.businessName && (
          <Text className="text-sm mt-0.5 text-muted-foreground">
            {item.businessName}
          </Text>
        )}
        {item.description && (
          <Text
            className="text-sm mt-1 text-muted-foreground"
            numberOfLines={2}
          >
            {item.description}
          </Text>
        )}
        <Text className="text-xs mt-1 text-muted-foreground">
          Submitted {formattedDate}
        </Text>
        {item.checkInCount !== undefined && item.checkInCount > 0 && (
          <Text className="text-xs mt-0.5 text-muted-foreground">
            {item.checkInCount} check-in{item.checkInCount !== 1 ? 's' : ''}
          </Text>
        )}
      </View>

      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Pressable
          onPress={() => onApprove(item.id)}
          accessibilityRole="button"
          accessibilityLabel="Approve item"
          className="bg-primary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <Ionicons name="checkmark" size={18} className="text-background" />
          <Text className="text-sm font-semibold text-background">
            Approve
          </Text>
        </Pressable>

        <Pressable
          onPress={() => onEdit(item.id)}
          accessibilityRole="button"
          accessibilityLabel="Edit item"
          className="bg-surface-secondary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
          }}
        >
          <Ionicons name="create-outline" size={18} className="text-foreground" />
          <Text className="text-sm font-semibold text-foreground">Edit</Text>
        </Pressable>

        <Pressable
          onPress={handleDelete}
          disabled={hasCheckIns}
          accessibilityRole="button"
          accessibilityLabel="Delete item"
          className="bg-surface-secondary"
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            paddingVertical: 8,
            gap: 4,
            opacity: hasCheckIns ? 0.4 : 1,
          }}
        >
          <Ionicons name="trash-outline" size={18} className="text-error" />
          <Text className="text-sm font-semibold text-error">Delete</Text>
        </Pressable>
      </View>
    </View>
  );
}
